import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/24/solid';

const ALL_CALENDARS_VALUE = null;
const ALL_CALENDARS_LABEL = 'All calendars';

/**
 * Toolbar wrapper — same breakpoints as Planner Calendar header so dropdown trigger/panel align across tabs.
 */
export const CALENDAR_FILTER_DROPDOWN_WRAP_CLASSNAME =
  'max-w-[9rem] shrink min-w-0 sm:max-w-[14rem] md:max-w-[min(18rem,100%)]';

/** Aligns to trigger’s right; grows with content (`w-max`), modest floor so it doesn’t look oversized. */
const DROPDOWN_PANEL_BASE =
  'absolute z-[100] mt-2 max-h-[min(24rem,70vh)] overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-100 ' +
  'left-auto right-0 min-w-[min(15rem,calc(100vw-2rem))] w-max max-w-[calc(100vw-2rem)]';

function MultiSummaryDots({ calendars, ids }) {
  const calById = useMemo(() => {
    const m = new Map();
    calendars.forEach((c) => m.set(c.id, c));
    return m;
  }, [calendars]);
  const toShow = (ids ?? []).slice(0, 4);
  return (
    <span className="flex items-center shrink-0 -space-x-1 mr-1" aria-hidden>
      {toShow.map((id) => {
        const c = calById.get(id);
        return (
          <span
            key={id}
            className="w-4 h-4 rounded-full ring-2 ring-white"
            style={{ backgroundColor: c?.color ?? '#94a3b8' }}
          />
        );
      })}
    </span>
  );
}

/** multiple=false: value is number|null; onChange(number|null).
 *  multiple=true: value is number[]|null — null = all calendars visible; [] = none.
 * @param {boolean} [fullWidthTrigger=true] — quando false (ex. cabeçalho Planner), trigger só ocupa até max-w (~280px) e não estica até à borda direita.
 */
export default function CalendarFilterDropdown({
  calendars = [],
  value,
  onChange,
  className = '',
  multiple = false,
  fullWidthTrigger = true,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedCalendar =
    multiple || value === ALL_CALENDARS_VALUE
      ? ALL_CALENDARS_LABEL
      : calendars.find((c) => c.id === value)?.name ?? ALL_CALENDARS_LABEL;

  const multiLabelFull = useMemo(() => {
    if (!multiple) return null;
    if (value === null) return ALL_CALENDARS_LABEL;
    if (!Array.isArray(value)) return ALL_CALENDARS_LABEL;
    if (value.length === 0) return 'No calendars';
    if (value.length === 1) {
      const c = calendars.find((x) => x.id === value[0]);
      return c?.name ?? '1 calendar';
    }
    return `${value.length} calendars`;
  }, [multiple, value, calendars]);

  /** Mobile-only: count for subset; null when “all” (icon only). */
  const multiLabelMobileCount = useMemo(() => {
    if (!multiple) return null;
    if (value === null) return null;
    if (!Array.isArray(value)) return null;
    return String(value.length);
  }, [multiple, value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSingle = (id) => {
    onChange(id === ALL_CALENDARS_VALUE ? null : id);
    setOpen(false);
  };

  /** multiple only */
  const allCalendarIdsSorted = () =>
    calendars.map((c) => c.id).sort((a, b) => a - b);

  const toggleMultiCalendarId = (calId) => {
    const allIds = allCalendarIdsSorted();
    if (value === null) {
      const next = allIds.filter((id) => id !== calId);
      onChange(next);
      return;
    }
    if (!Array.isArray(value)) {
      return;
    }
    const has = value.includes(calId);
    let next = has ? value.filter((id) => id !== calId) : [...value, calId].sort((a, b) => a - b);
    if (next.length === 0) {
      onChange([]);
      return;
    }
    const coversAll =
      next.length === allIds.length && allIds.length > 0 && allIds.every((id) => next.includes(id));
    if (coversAll) {
      onChange(null);
      return;
    }
    onChange(next);
  };

  const multiRowChecked = (calId) => {
    if (value === null) return true;
    if (!Array.isArray(value)) return false;
    return value.includes(calId);
  };

  /** Master checked only when not filtering (`null`): every calendar appears. Uncheck clears to `[ ]`. */
  const handleMasterChange = (e) => {
    if (e.target.checked) {
      onChange(null);
    } else {
      onChange([]);
    }
  };

  const buttonAriaLabel = useMemo(() => {
    if (multiple) {
      return multiLabelFull ? `Filter calendars: ${multiLabelFull}` : 'Filter calendars';
    }
    return `Filter calendars: ${selectedCalendar}`;
  }, [multiple, multiLabelFull, selectedCalendar]);

  const compactTrigger = !fullWidthTrigger;

  const rootClass =
    fullWidthTrigger
      ? `relative w-full ${className}`.trim()
      : `relative w-fit max-w-full min-w-0 shrink ${className}`.trim();

  const btnPadding = compactTrigger
    ? 'px-2 py-1 sm:px-3 gap-1.5 sm:gap-2'
    : 'px-3 gap-2';

  return (
    <div ref={containerRef} className={rootClass}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex min-w-0 w-full items-center justify-center ${btnPadding} py-1 bg-white border border-gray-300 rounded-full shadow-sm text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={buttonAriaLabel}
      >
        {multiple ? (
          <>
            {value !== null && Array.isArray(value) && value.length > 0 ? (
              <MultiSummaryDots calendars={calendars} ids={value} />
            ) : (
              <CalendarDaysIcon className="h-5 w-5 text-gray-600 shrink-0" />
            )}
            {multiLabelMobileCount != null && (
              <span className="sm:hidden tabular-nums font-semibold text-gray-700 shrink-0 min-w-[1ch] text-center">
                {multiLabelMobileCount}
              </span>
            )}
            <span className="truncate min-w-0 text-left flex-1 hidden sm:inline">{multiLabelFull}</span>
          </>
        ) : (
          <>
            {typeof value === 'number' && calendars.some((c) => c.id === value) ? (
              <span
                className="w-4 h-4 rounded-full shrink-0"
                style={{
                  backgroundColor:
                    calendars.find((c) => c.id === value)?.color ?? '#94a3b8',
                }}
                aria-hidden
              />
            ) : (
              <CalendarDaysIcon className="h-5 w-5 text-gray-600 shrink-0" />
            )}
            <span className="truncate min-w-0 hidden sm:inline">{selectedCalendar}</span>
          </>
        )}
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-600 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        (!multiple ? (
          <ul
            className={`${DROPDOWN_PANEL_BASE} pt-3 pb-0`}
            role="listbox"
          >
            <li
              role="option"
              aria-selected={value === ALL_CALENDARS_VALUE}
              onClick={() => handleSelectSingle(ALL_CALENDARS_VALUE)}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-gray-800"
            >
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
              <span>{ALL_CALENDARS_LABEL}</span>
            </li>
            {calendars.map((cal) => (
              <li
                key={cal.id}
                role="option"
                aria-selected={value === cal.id}
                onClick={() => handleSelectSingle(cal.id)}
                className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-gray-800"
              >
                <span
                  className="w-3 h-3 rounded-full shrink-0 mt-1.5"
                  style={{ backgroundColor: cal.color || '#94a3b8' }}
                  aria-hidden
                />
                <span className="leading-snug whitespace-normal break-words min-w-0">{cal.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ul
            className={`${DROPDOWN_PANEL_BASE} py-2 pl-2 pr-1`}
            role="listbox"
            aria-multiselectable="true"
          >
            <li className="px-3 py-2 border-b border-gray-100 mb-1">
              <label className="flex items-center gap-2 cursor-pointer text-gray-800 font-medium select-none">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={value === null}
                  onChange={handleMasterChange}
                  onClick={(e) => e.stopPropagation()}
                />
                <CalendarDaysIcon className="h-5 w-5 text-gray-400 shrink-0" />
                <span>{ALL_CALENDARS_LABEL}</span>
              </label>
            </li>
            {calendars.map((cal) => (
              <li key={cal.id} className="px-3 py-1.5 hover:bg-blue-50/60 rounded-lg mx-1">
                <label className="flex items-start gap-2 cursor-pointer text-gray-800 select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                    checked={multiRowChecked(cal.id)}
                    onChange={() => toggleMultiCalendarId(cal.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span
                    className="w-3 h-3 rounded-full shrink-0 mt-1"
                    style={{ backgroundColor: cal.color || '#94a3b8' }}
                    aria-hidden
                  />
                  <span className="leading-snug whitespace-normal break-words min-w-0 flex-1">
                    {cal.name}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}
